import { useIsSpecialtyBackendTranslationEnabled } from '@/features/flags';
import { SpecialtyCountryUnsupportedError } from '@/hooks/query/specialty-country-unsupported-error';
import { useTranslationHelpers } from '@/hooks/useTranslationHelpers';
import { type ClinicianTypeInput } from '@/lib/types';
import { getSpecialties } from '@/pages/api/scribeDemoApi';
import { logger } from '@/utils/logger/logger';
import { reactQueryClient } from '@/utils/reactQueryClient';
import { toast } from '@heidi/design-system/toast';
import { useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useCallback, useRef } from 'react';

// paths
export const GET_SPECIALTIES = `Get_specialties`;
const SHOULD_SHOW_TOAST = false;

/**
 * A specialty as the app consumes it: the wire item plus a guaranteed-renderable
 * display name.
 *
 * - `name` is the canonical English identifier — writes, URL params, logic
 *   keys, analytics. Never render it.
 * - `localizedName` is the display name in the active language — the ONLY
 *   field render sites use.
 */
export type Specialty = ClinicianTypeInput & { localizedName: string };

export type QuerySpecialtiesResponse = {
  /**
   * The single list: canonical `name` + display `localizedName`, sorted by
   * `localizedName` with `localeCompare(activeLanguage)`.
   */
  specialties: Specialty[];
  total: number;
};

/** Raw API payload — localization is applied in the query `select`, not at fetch time. */
type RawSpecialtiesResponse = { specialties: ClinicianTypeInput[]; total: number };

export { SpecialtyCountryUnsupportedError };

export function useQuerySpecialties<T = QuerySpecialtiesResponse>(
  select?: (data: QuerySpecialtiesResponse) => T,
  options?: { country?: string; enabled?: boolean }
) {
  const { getTranslatedSpecialty, activeLanguage } = useTranslationHelpers();
  const isBackendTranslationEnabled = useIsSpecialtyBackendTranslationEnabled();

  // The backend-translation flag chooses the SOURCE of `localizedName`,
  // nothing else. ON: the backend already resolved the account language into
  // `localized_name` — falling back to `name` when the field is missing
  // (stale/on-prem backends that predate it) or empty/whitespace (an untranslated
  // gap must render the English name, never a blank option). OFF: the i18next
  // catalog, with the translation-key-echo guard centralized here so no
  // consumer ever renders a raw `specialties.<Key>` path.
  const localize = useCallback(
    (item: ClinicianTypeInput): string => {
      if (isBackendTranslationEnabled) return item.localized_name?.trim() || item.name;
      const translated = getTranslatedSpecialty(item.name);
      return translated.startsWith('specialties.') ? item.name : translated;
    },
    [isBackendTranslationEnabled, getTranslatedSpecialty]
  );

  // Server order carries no display meaning — the FE owns ordering in both
  // flag states, locale-aware so accented names collate correctly.
  const toSpecialties = useCallback(
    (items: ClinicianTypeInput[]): Specialty[] =>
      items
        .map((item) => ({ ...item, localizedName: localize(item) }))
        .sort((a, b) => a.localizedName.localeCompare(b.localizedName, activeLanguage)),
    [localize, activeLanguage]
  );

  // Localize in `select`, not `queryFn`: dictate onboarding fetches specialties
  // while the scoped i18next bundle is still loading (English `t()`). Baking
  // names into cached query data freezes that first language even after the OS
  // locale instance mounts.
  //
  // Most consumers pass an inline `select`, which React Query re-runs every
  // render. Memoize the view by (raw data, transform fn): `raw` is
  // referentially stable via structural sharing; the transform changes
  // identity on a language change or a flag flip.
  const viewRef = useRef<{
    raw: RawSpecialtiesResponse;
    fn: typeof toSpecialties;
    out: QuerySpecialtiesResponse;
  } | null>(null);

  const toView = useCallback(
    (raw: RawSpecialtiesResponse): QuerySpecialtiesResponse => {
      const cached = viewRef.current;
      if (cached && cached.raw === raw && cached.fn === toSpecialties) {
        return cached.out;
      }
      const out: QuerySpecialtiesResponse = {
        specialties: toSpecialties(raw.specialties),
        total: raw.total,
      };
      viewRef.current = { raw, fn: toSpecialties, out };
      return out;
    },
    [toSpecialties]
  );

  const selectWithView = useCallback(
    (raw: RawSpecialtiesResponse): T => {
      const data = toView(raw);
      return select ? select(data) : (data as T);
    },
    [select, toView]
  );

  const country = options?.country;

  return useQuery<RawSpecialtiesResponse, Error, T>({
    // The locale segment is cache identity and rides the request as
    // `Accept-Language` (see `getSpecialties`) — a language change refetches,
    // per-language responses stay separate, and pre-account onboarding
    // surfaces get the on-screen locale instead of the browser default. The
    // key is flag-independent: a cold load fetches once under its final key;
    // a settling flag only re-runs `select`.
    queryKey: [
      GET_SPECIALTIES,
      { ...(country ? { country } : {}), locale: activeLanguage },
    ],
    enabled: options?.enabled,
    select: selectWithView,
    // A language or country change is a new key → cache miss → refetch. On a
    // locale-only re-key the previous list rides along as placeholder so
    // pickers never empty mid-switch — same items, the display names swap in
    // place when the new list lands. A country re-key changes the CONTENT:
    // holding the old country's list would let a user pick a specialty that is
    // invalid for the new country (and paper over a 422), so that transition
    // falls through to a real pending state instead.
    placeholderData: (previousData, previousQuery) => {
      const previousCountry = (
        previousQuery?.queryKey[1] as { country?: string } | undefined
      )?.country;
      return previousCountry === country ? previousData : undefined;
    },
    // 422 on a scoped fetch is deterministic (FE/BE country-list mismatch) — retrying is
    // pointless. Other errors keep the app-wide default of 1 retry (reactQueryClient) —
    // a query-level `retry` replaces that default, so the cap is restated here.
    retry: (failureCount, error) =>
      !(error instanceof SpecialtyCountryUnsupportedError) && failureCount < 1,
    queryFn: async (): Promise<RawSpecialtiesResponse> => {
      try {
        const { data } = await getSpecialties(country, activeLanguage);
        return {
          specialties: data.specialties,
          total: data.total,
        };
      } catch (error) {
        if (country && error instanceof AxiosError && error.response?.status === 422) {
          // Fail early and loud — never fall back to the global list silently: a 422
          // means the FE country list is ahead of the backend CountryCode enum.
          logger.error(
            `[specialties] backend rejected country "${country}" (422) — FE country list is ahead of the backend CountryCode enum`
          );
          throw new SpecialtyCountryUnsupportedError(country);
        }
        if (error instanceof AxiosError) {
          if (SHOULD_SHOW_TOAST) {
            toast({
              tone: 'destructive',
              title: 'Error',
              description: JSON.stringify(error.response?.data),
            });
          }
        } else {
          if (SHOULD_SHOW_TOAST) {
            toast({
              tone: 'destructive',
              title: 'Error',
              description: 'Error in getting specialties, please try again later',
            });
          }
        }
        throw new Error('Failed to fetch specialties');
      }
    },
  });
}

/**
 * Refetch every cached specialties list (all locale/country keys, by prefix).
 */
export const invalidateSpecialties = async () => {
  try {
    await reactQueryClient.invalidateQueries({ queryKey: [GET_SPECIALTIES] });
  } catch {
    logger.error('[specialties] failed to invalidate the specialties cache');
  }
};
